import { useState, useEffect, useRef, useContext } from 'react';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import { Form, Formik, setNestedObjectValues } from 'formik';
import SubmissionSummary from './SubmissionSummary';
import Yup from 'utils/validation/yup-extensions';
import {
	UAR_USER_PROFILE_KEYS,
	UAR_USER_PROFILE_FIELDS,
	UAR_STEPPER_FIELDS,
	UAR_STEPPER_VALUES,
} from './_const';
import {
	getUserData,
	UpdateUserInfo,
	addUserRoles,
	deleteUserRoles,
	getUserRoles,
} from './_api';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import FormFields from './FormFields';
import FormLayout from 'layouts/FormLayout';
import HelpBar, {
	Main,
} from 'pages/ApplicationRequest/components/containers/1616BridgesEpic/components/HelpBar';
import HelpLayout from 'layouts/HelpLayout';
import FixedBottomNavigation from 'pages/AnnualUserAccessReview/components/FixedBottomNavigation';
import {
	navigate,
	processStaffDataForTable,
} from 'pages/AnnualUserAccessReview/_util';
import SelectUsers from 'pages/AnnualUserAccessReview/components/SelectUsers';
import { UserContext } from 'context/User/UserContext';
import { fetchBasicStaffList } from 'pages/AnnualUserAccessReview/_api';

const EditUserProfile = ({ ...props }) => {
	const [activeStep, setActiveStep] = useState(0);
	const [stepProgress, setStepProgress] = useState(0);
	const history = useHistory();
	const formikRef = useRef();

	const [userToEditFIID, setUserToEditFIID] = useState();
	const [sOpen, setOpen] = useState(false);
	const [userData, setUserData] = useState(undefined);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isEmployee, setIsEmployee] = useState(undefined);
	const [isSiteAdmin, setIsSiteAdmin] = useState(false);

	const siteAdminRoleId = 1612;

	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const user =
		contextUser.userInfo.impersonationProfile ??
		contextUser.userInfo.userProfile;

	const mapLoggedInUserForUar = sourceUser => {
		if (!sourceUser) {
			return null;
		}

		const displayName = sourceUser.name ?? sourceUser.Name ?? '';
		const splitName = displayName
			.split(',')
			.map(part => part.trim())
			.filter(Boolean);

		return {
			...sourceUser,
			esoFiId:
				sourceUser.esoFiId ?? sourceUser.ID ?? sourceUser.id ?? null,
			upn: sourceUser.upn ?? sourceUser.UPN ?? null,
			networkID: sourceUser.networkID ?? sourceUser.NetworkID ?? null,
			firstName:
				sourceUser.firstName ??
				sourceUser.FirstName ??
				splitName?.[1] ??
				null,
			lastName:
				sourceUser.lastName ??
				sourceUser.LastName ??
				splitName?.[0] ??
				null,
			name: sourceUser.name ?? sourceUser.Name ?? null,
		};
	};

	// Call GetUserData API on load to pre-load information
	useEffect(() => {
		async function fetch() {
			const userData = await getUserData(userToEditFIID);
			const siteAdminRoleData = await getUserRoles(siteAdminRoleId);

			const userHasRole = siteAdminRoleData?.some(
				user => user.ID === userToEditFIID,
			);
			const data = { ...userData, siteAdministrator: userHasRole };
			setUserData(data);
			setIsEmployee(data?.type === 'employee' ? true : false);
			setIsSiteAdmin(userHasRole);
			setFormikFieldValues('defaultValue', formikRef?.current, data);
		}
		if (userToEditFIID) {
			fetch();
		}
	}, [userToEditFIID]);

	const [sStaff, setStaff] = useState([]);

	const setFormikFieldValues = (propName, formik, data) => {
		Object.entries(UAR_USER_PROFILE_FIELDS).forEach(([key, value]) => {
			if (propName === 'defaultValue') {
				formik?.setFieldValue(
					key,
					data?.[key] !== undefined && data?.[key] !== null
						? data?.[key]
						: (value[propName] ?? ''),
				);
			} else {
				formik?.setFieldValue(key, value[propName] ?? '');
			}
		});
		formikRef?.current?.setErrors({});
	};

	const extractValuesObject = propName => {
		const obj = {};
		Object.entries(UAR_USER_PROFILE_FIELDS).forEach(([key, value]) => {
			if (!(key in obj)) {
				if (propName === 'defaultValue') {
					obj[key] =
						userData?.[key] !== undefined &&
						userData?.[key] !== null
							? userData?.[key]
							: (value[propName] ?? '');
				} else {
					obj[key] = value[propName] ?? '';
				}
			}
		});
		return obj;
	};

	var initialValues = extractValuesObject('defaultValue');

	if (initialValues) {
		formikRef?.current?.setTouched(
			setNestedObjectValues(initialValues, true),
		);
	}

	const updatedInitialValues = {
		...initialValues,
		[UAR_USER_PROFILE_KEYS.SITE_ADMINISTRATOR]: !!isSiteAdmin,
	};

	// set default value

	const validationSchema = (() => {
		// Get the existing validation rules
		const existingValidationSchema =
			extractValuesObject('validationSchema');

		// Create a new validation schema that includes all the existing rules
		// and replaces the rule for UAR_STEPPER_FIELDS.USER_TYPE
		// We would get these values from an API instead of hard coded
		const newValidationSchema = Yup.object().shape({
			...existingValidationSchema,
		});

		return newValidationSchema;
	})();

	useEffect(() => {
		let active = true;

		async function fetchApi() {
			//fetch the base job category info
			const response = await fetchBasicStaffList(user.ID);
			const { values } = response;
			const data = processStaffDataForTable(values);
			//set the state
			setStaff(data);
		}

		if (active && user.ID) {
			fetchApi();
		}

		return () => {
			active = false;
		};
	}, [user]);

	const SubmitForm = async () => {
		const hasValidationErrors =
			Object.keys(formikRef?.current?.errors ?? {}).length > 0;

		const loggedInUser = mapLoggedInUserForUar(user);

		if (hasValidationErrors || !loggedInUser) {
			return;
		}

		//translate the form data values back to the original profile

		let siteAdminChecked =
			formikRef?.current?.values[
				UAR_USER_PROFILE_KEYS.SITE_ADMINISTRATOR
			];
		const userToModifyId =
			formikRef?.current?.values?.['User to Edit']?.[0]?.fiId;
		const payload = {
			loggedInUser,
			targetUser: {
				...formikRef?.current?.values,
				esoFiId:
					formikRef?.current?.values?.['User to Edit']?.[0]?.fiId,
			},
		};

		setIsSubmitting(true);

		await UpdateUserInfo(payload).then(resp => {
			if (siteAdminChecked) {
				addUserRoles(siteAdminRoleId, userToModifyId);
			} else {
				deleteUserRoles(siteAdminRoleId, userToModifyId);
			}
			setIsSubmitting(false);
		});
	};

	const CancelForm = () => {
		let tempData = userData;
		setUserData(tempData);
	};

	const GoHome = () => {
		history.goBack();
	};

	const onNavigate = type => {
		let newStep = navigate(
			type,
			activeStep,
			stepLabels.length,
			SubmitForm,
			CancelForm,
			GoHome,
		);
		setStepProgress(Math.max(newStep, stepProgress));
		setActiveStep(newStep);
	};

	const stepLabels = Object.values(UAR_STEPPER_FIELDS).filter(label => {
		// User IDS excluded from requiring the user type field
		// 7 -- employee
		// 9 -- service account
		// 10 -- sys account
		const excludedUserIds = [7, 9, 10];
		const userId = formikRef?.current?.values[UAR_USER_PROFILE_KEYS.TYPEID];
		return (
			!excludedUserIds.includes(Number(userId?.toString())) ||
			label !== UAR_STEPPER_FIELDS.USER_TYPE
		);
	});

	const open = useSelector(state => state.help.show);

	const checkVal = val => {
		return (
			val !== 'Regulatory User' &&
			val !== 'Tenant Badge Only' &&
			val !== '' &&
			val !== undefined
		);
	};

	// If the user type is set to a particular value, make the alternate email required
	useEffect(() => {
		if (
			checkVal(
				formikRef?.current?.values[UAR_USER_PROFILE_KEYS.USER_TYPE],
			)
		) {
			formikRef?.current?.setFieldTouched(
				UAR_USER_PROFILE_KEYS.ALTERNATE_EMAIL,
				true,
			);
		}
	}, [formikRef?.current?.values[UAR_USER_PROFILE_KEYS.USER_TYPE]]);

	const renderStepContent = (step, formik) => {
		const containsUserType = stepLabels.includes(
			UAR_STEPPER_FIELDS.USER_TYPE,
		);
		switch (step) {
			case 0:
				return (
					<SelectUsers
						singleSelection={true}
						key={'step_' + activeStep}
						name={'User to Edit'}
						formik={formik}
						directReports={sStaff}
						selectUser={setUserToEditFIID}
					/>
				);
			case 1:
				return (
					<FormFields
						key={'step_' + activeStep}
						{...{ formik, validationSchema, userData, isEmployee }}
						section={UAR_STEPPER_FIELDS.PERSONNEL_INFORMATION}
					/>
				);
			case 2:
				return (
					<FormFields
						key={'step_' + activeStep}
						{...{ formik, validationSchema, userData, isEmployee }}
						section={UAR_STEPPER_FIELDS.HOME_ADDRESS}
					/>
				);
			case 3:
				return containsUserType ? (
					<FormFields
						key={'step_' + activeStep}
						{...{ formik, validationSchema, userData, isEmployee }}
						section={UAR_STEPPER_FIELDS.USER_TYPE}
					/>
				) : (
					<SubmissionSummary
						firstName={
							formik?.values['User to Edit']?.[0]?.firstName
						}
						lastName={formik?.values['User to Edit']?.[0]?.lastName}
						key={'step_' + activeStep}
						name={UAR_STEPPER_FIELDS.SUBMIT}
						formik={formik}
						isSubmitting={isSubmitting}
					/>
				);
			case 4:
				return (
					<SubmissionSummary
						firstName={
							formik?.values['User to Edit']?.[0]?.firstName
						}
						lastName={formik?.values['User to Edit']?.[0]?.lastName}
						key={'step_' + activeStep}
						name={UAR_STEPPER_FIELDS.SUBMIT}
						formik={formik}
						isSubmitting={isSubmitting}
					/>
				);
			default:
				return <>Default</>;
		}
	};

	const filteredFieldsForCurrentStep = Object.entries(UAR_USER_PROFILE_FIELDS)
		.filter(([key, value]) => {
			const currentField = UAR_STEPPER_VALUES.find(
				stepObj => stepObj.step === activeStep,
			)?.field.value;
			return value.section === currentField;
		})
		.map(([key, value]) => key);

	const hasErrorsInCurrentStep = (names, formik) => {
		return names.some(name => name in formik.errors);
	};

	const hasErrors = formik => {
		return hasErrorsInCurrentStep(filteredFieldsForCurrentStep, formik);
	};

	const isStepError = (formik, index) => {
		const names = Object.keys(UAR_USER_PROFILE_KEYS);

		return names in formik?.errors && names in formik?.touched;
	};

	const isStepFailed = (formik, index) => {
		let fields = Object.keys(
			Object.fromEntries(
				Object.entries(UAR_USER_PROFILE_FIELDS).filter(
					([key, value]) =>
						value.section === UAR_STEPPER_VALUES[index].field.value,
				),
			),
		);
		let val = fields?.some(n => {
			return (
				(n in formik?.errors && n in formik?.touched) ||
				(n in formik?.errors &&
					n === UAR_USER_PROFILE_KEYS.ALTERNATE_EMAIL)
			);
		});

		return val && index <= activeStep ? true : false;
	};

	return (
		<Formik
			initialValues={updatedInitialValues}
			validationSchema={validationSchema}
			validateOnMount
			innerRef={formikRef}
			onSubmit={SubmitForm}
		>
			{formik => (
				<Main open={sOpen}>
					<Form>
						<FormLayout
							title={'Edit User Profile'}
							setOpen={() => setOpen(true)}
							open={sOpen}
						>
							{/* <Box sx={{ mt: 3 }}> */}
							<Stepper activeStep={activeStep} alternativeLabel>
								{stepLabels.map((label, index) => {
									const labelProps = {};
									if (isStepFailed(formik, index)) {
										labelProps.optional = (
											<Typography
												variant='caption'
												color='error'
											>
												Information Missing
											</Typography>
										);

										labelProps.error = true;
									}
									if (isStepError(formik, index)) {
										labelProps.optional = (
											<Typography
												variant='caption'
												color='error'
											>
												Alert message
											</Typography>
										);
										labelProps.error = true;
									}
									return (
										<Step key={'step_' + index}>
											<StepLabel
												onClick={() => {
													if (activeStep > index) {
														setActiveStep(index);
													}
												}}
												{...labelProps}
											>
												{label}
											</StepLabel>
										</Step>
									);
								})}
							</Stepper>
							{renderStepContent(activeStep, formik)}
							<FixedBottomNavigation
								onChange={onNavigate}
								currentStep={activeStep}
								isSubmitting={isSubmitting}
								keepSubmitDisabled={
									activeStep < stepLabels.length - 2 ||
									Object.keys(formik.errors).length > 0
								}
								keepNextDisabled={
									activeStep === stepLabels.length - 2
								}
								stepCount={stepLabels.length}
								hasErrors={
									hasErrors(formik) ||
									isStepError(formik, activeStep)
								}
								open={open}
							/>
						</FormLayout>
						<HelpBar open={sOpen} setOpen={setOpen}>
							<HelpLayout title='Edit User Profile'>
								<Typography>
									Managers can Update an employee or non
									employee's information.
									<br /> <br />
									<strong>NOTE: </strong>
									<p>
										Changes to <b>Job Title</b>,{' '}
										<b>Cost Center</b>, <b>Department</b>,{' '}
										<b>Manager's Email</b>, and{' '}
										<b>Address</b> for UPMC Employees and
										Contingent Workers will be temporary.
										These modifications will be overwritten
										by HR Direct on a nightly basis. All
										permanent changes to these values will
										need to be changed through Human
										Resources.
									</p>
								</Typography>
								<Typography>
									For more information, please check on{' '}
									<a
										href='https://infonet.upmc.com/infonet/ls/content/6236583790140711/annual-user-access-review-guide-pdf-1764799965'
										target='_blank'
										rel='noopener noreferrer'
									>
										InfoNet
									</a>
								</Typography>
							</HelpLayout>
						</HelpBar>
					</Form>
				</Main>
			)}
		</Formik>
	);
};

export default EditUserProfile;
